// PM "Budget" tab — combines the budget-vs-actual panel, the live back-costing
// breakdown (labour/fleet/gear/materials/invoices) and the supplier invoices
// uploaded against this job, all in one place.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SupplierItems } from "@/components/invoices/SupplierItems";
import api from "@/lib/api";
import { JobBudgetPanel } from "@/components/JobBudgetPanel";
import { JobCostCard } from "@/components/JobCostCard";
import { FileText, ExternalLink, Upload } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CLS = {
  auto_linked: { label: "Auto-linked", cls: "bg-green-600 text-white" },
  confirmed: { label: "Confirmed", cls: "bg-[#FF5F15] text-white" },
  pending_review: { label: "Review", cls: "bg-yellow-400 text-black" },
  archived: { label: "Archived", cls: "bg-zinc-300 text-zinc-700" },
};

export function JobUploadedInvoices({ jobId }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/invoices", { params: { job_id: jobId } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [jobId]);

  const tok = typeof window !== "undefined" ? localStorage.getItem("fl_token") || "" : "";
  const fileLink = (path) => `${api.defaults.baseURL}/files/view?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(tok)}&download=1`;
  const total = (items || []).filter((i) => i.status !== "archived").reduce((s, i) => s + Number(i.total || 0), 0);

  return (
    <section className="hard-card p-5" data-testid={`pm-uploaded-invoices-${jobId}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-tag flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Uploaded supplier invoices
          </div>
          <div className="text-[10px] mt-1 font-mono uppercase tracking-widest text-zinc-500">
            {(items || []).length} on this job · {money(total)} document total · includes bills under review
          </div>
        </div>
        <Link
          to={`/app/invoices?job_id=${encodeURIComponent(jobId)}`}
          className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-[#09090B] text-white px-3 py-2 hover:bg-zinc-800 transition-colors"
          data-testid={`pm-upload-invoice-link-${jobId}`}
        >
          <Upload className="w-3.5 h-3.5" /> Upload / manage
        </Link>
      </div>

      {items === null ? (
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500" data-testid={`pm-invoices-empty-${jobId}`}>
          No supplier invoices uploaded against this job yet — scan one from the Invoices page and it rolls into the cost breakdown above.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b-2 border-zinc-900">
                <th className="py-2 pr-3">Vendor</th>
                <th className="py-2 pr-3">Invoice #</th>
                <th className="py-2 pr-3 hidden sm:table-cell">Date</th>
                <th className="py-2 pr-3 text-right">Total</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const st = STATUS_CLS[inv.status] || { label: inv.status || "—", cls: "bg-zinc-200 text-zinc-700" };
                return (
                  <tr key={inv.id} className="border-b border-zinc-200" data-testid={`pm-invoice-row-${inv.id}`}>
                    <td className="py-2.5 pr-3"><strong>{inv.vendor || "Unknown vendor"}</strong>{inv.line_items?.length > 0 && <details className="mt-1 min-w-[220px]"><summary className="text-xs text-[#FF5F15] cursor-pointer">Items &amp; deliveries</summary><div className="pt-2"><SupplierItems items={inv.line_items} currency={inv.currency} matches={inv.fleet_matches} reviewed={inv.operational_reviewed} /></div></details>}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs">{inv.invoice_number || "—"}</td>
                    <td className="py-2.5 pr-3 hidden sm:table-cell font-mono text-xs">{inv.invoice_date || "—"}</td>
                    <td className="py-2.5 pr-3 text-right font-mono font-bold">{money(inv.total)}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="py-2.5 text-right">
                      {inv.file_path && (
                        <a
                          href={fileLink(inv.file_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-[#FF5F15]"
                          data-testid={`pm-invoice-view-${inv.id}`}
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function PmBudgetTab({ jobId }) {
  return (
    <div className="space-y-6" data-testid={`pm-budget-tab-${jobId}`}>
      <JobBudgetPanel jobId={jobId} />
      <JobCostCard jobId={jobId} />
      <JobUploadedInvoices jobId={jobId} />
    </div>
  );
}
